import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { boolean as booleanYup, object as objectYup, string as stringYup } from 'yup';
import getValue from 'lodash/get';
import useInitialErrors from '../../../hooks/formik-initial-errors';
import { ManagerFormsType, ManagerRequestType, SocialEnum } from '../../../types/auth';
import { authService } from '../../../lib/api/auth';
import { deleteReduxProfile } from '../../../redux/registration';
import { goToRoom } from './../utils';
import ReCAPTCHA from 'react-google-recaptcha';
import { getSiteKey, isConsentFeatureEnabled } from '../../../lib/utils/common-functions';
import { registrationLocationConstants } from '../../../lib/constants/constants';
import CountryDropdown from '../../../components/dropdown/country';
import StateDropdown from '../../../components/dropdown/states';
import CityDropdown from '../../../components/dropdown/city';
import LegalDocumentModal from '../../room/components/LegalDocumentModal';
import { Link, useNavigate } from 'react-router-dom';
import { routes } from '../../routes/routes-names';
import { getConsentWorkflowSettings } from '../../../redux/consentWorkFlow';
import PhoneCodeDropdown from '../../../components/dropdown/phoneCode';
import { emailRegexp } from '../../../lib/utils/validation';
import secureLocalStorage from 'react-secure-storage';
import { userCacheDataService } from '../../../lib/api/userCacheData';
import Swal from 'sweetalert2';
import { isCaptchaEnabled, USER_DATA_CACHE_KEY } from '../../../config/constants';
import {
    sessionStorageClearAll,
    sessionStorageGetItem,
    sessionStorageRemoveItem,
} from '../../../components/storage/sessionStorageManager';
import { localStorageGetItem, localStorageRemoveItem } from '../../../components/storage/localStorageManager';
import { useConsentFlow, useUserProfile } from '../../../hooks/useSelector';
import { InfoIcon, Message } from '../../../lib/constants/messages';
import { CompanyStatus, CompanyType } from '../../../types/claimed';
import { useAppDispatch } from '../../../redux/reduxhooks';
import UnsavedChanges from '../../../components/registration/UnsavedChanges';

type Props = {
    managerId?: string;
    handleSelect: (key: string, key2: string) => void;
    selectedAuthType?: string;
    hasUnsavedChanges: (key: boolean) => void;
};

const ManagerSingleRegistrationPage: React.FC<Props> = ({
    managerId,
    handleSelect,
    selectedAuthType,
    // hasUnsavedChanges,
}) => {
    const dispatch = useAppDispatch();
    const consentWorkFlowSettings = useConsentFlow();
    const reduxProfile = useUserProfile();
    const [sessionData, setSessionData] = useState<any>();
    const [sessionDataStep3, setSessionDataStep3] = useState<any>();
    const [stateDisabled, setStateDisabled] = useState(true);
    const [cityDisabled, setCityDisabled] = useState(true);
    const [iso2CountryString, setIso2CountryString] = useState('');
    const [iso2StateString, setIso2StateString] = useState('');
    const [passString, setPassString] = useState('');
    const [isStateValueAvailable, setIsStateValueAvailable] = useState(true);
    const [isCityValueAvailable, setIsCityValueAvailable] = useState(true);
    const [openLegalDocModal, setOpenLegalDocModal] = useState(false);
    const [selectedCountryId, setSelectedCountryId] = useState('');
    const [comPhoneCodeError, setComPhoneCodeError] = useState('');
    const [userPhoneCodeError, setUserPhoneCodeError] = useState('');
    const [useCacheData, setUseCacheData] = useState<boolean | undefined>();
    const [disableReferral, setDisableReferral] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const ref = useRef(null);
    const referralValue = localStorageGetItem('referral', true)!;
    const customEmail = localStorageGetItem('Email');

    const seoSearchPresent = localStorageGetItem('seosearch', false);

    const captchaEnabled = String(isCaptchaEnabled).toLowerCase() === 'true' ? true : false;

    const navigate = useNavigate();

    const initialFormData = {
        CompanyName: '',
        CompanyEmail: '',
        CompCountryCode: '',
        Phone: '',
        Website: '',
        country: '',
        state: '',
        CityDistrict: '',
        PostalCode: '',
        IsCompanyTermsChecked: false,
        recaptcha: '',
        OwnerPhoneNumber: '',
        OwnerCountryCode: '',
        // LinkedINUrl: '',
        Address2: '',
        CompanyAddress: '',
        ReferralCode: '',
    };

    const getValidationSchema = (
        isStateValueAvailable: boolean,
        isCityValueAvailable: boolean,
        captchaEnabled: boolean
    ) => {
        if (selectedAuthType !== 'customAuth') {
            return objectYup().shape({
                FirstName: stringYup().required('First name is required.'),
                LastName: stringYup().required('Last name is required.'),
                OwnerPhoneNumber: stringYup()
                    .required('Contact number is required and must be 10 digit number.')
                    .min(10, 'Contact number must be at least 10 digits.'),
                CompanyName: stringYup().required('Company name is required.'),
                Phone: stringYup()
                    .required('Contact number is required and must be 10 digit number.')
                    .min(10, 'Contact number must be at least 10 digits.'),
                CompanyEmail: stringYup()
                    .required('Company email is required.')
                    .matches(emailRegexp, { message: 'Invalid email format.' }),
                Website: stringYup().required('Company website is required.'),

                // CompanyAddress: stringYup().required('Flat / Building is required.'),
                // Address2: stringYup().required('Street address is required.'),
                PostalCode: stringYup().required('Postal / Zip code is required.'),
                agreement: booleanYup().oneOf([true]),
                IsCompanyTermsChecked: booleanYup().oneOf([true], 'Please accept terms and conditions to register.'),
                recaptcha: captchaEnabled
                    ? stringYup().nullable().required('Captcha is required.')
                    : stringYup().nullable(),
                country: stringYup().required('Country is required.'),
                CityDistrict: isCityValueAvailable ? stringYup().required('City is required.') : stringYup(),
                state: isStateValueAvailable ? stringYup().required('State is required.') : stringYup(),
            });
        } else {
            return objectYup().shape({
                CompanyName: stringYup().required('Company name is required.'),
                Phone: stringYup()
                    .required('Contact number is required and must be 10 digit number.')
                    .min(10, 'Contact number must be at least 10 digits.'),
                CompanyEmail: stringYup()
                    .required('Company email is required.')
                    .matches(emailRegexp, { message: 'Invalid email format.' }),
                Website: stringYup().required('Company website is required.'),

                // CompanyAddress: stringYup().required('Flat / Building is required.'),
                // Address2: stringYup().required('Street address is required.'),
                PostalCode: stringYup().required('Postal / Zip code is required.'),
                agreement: booleanYup().oneOf([true]),
                IsCompanyTermsChecked: booleanYup().oneOf([true], 'Please accept terms and conditions to register.'),
                recaptcha: captchaEnabled
                    ? stringYup().nullable().required('Captcha is required.')
                    : stringYup().nullable(),
                country: stringYup().required('Country is required.'),
                CityDistrict: isCityValueAvailable ? stringYup().required('City is required.') : stringYup(),
                state: isStateValueAvailable ? stringYup().required('State is required.') : stringYup(),
            });
        }
    };

    const sessionFormData = useMemo(() => {
        if (!sessionData?.cityDistrict) {
            setCityDisabled(true);
        }
        if (!sessionData?.state) {
            setStateDisabled(true);
        }

        return sessionData?.FirstName
            ? {
                  ...initialFormData,
                  FirstName: sessionData?.FirstName || reduxProfile?.firstName,
                  MiddleName: sessionData?.MiddleName,
                  LastName: sessionData?.LastName || reduxProfile?.lastName,
                  Email: sessionData?.Email || reduxProfile?.email,
                  CompanyName: sessionData?.CompanyName || sessionData?.name,
                  GstNumber: sessionData?.GstNumber,
                  CompanyEmail: sessionData?.CompanyEmail || sessionData?.companyEmail,
                  // LinkedINUrl: sessionData?.LinkedINUrl,
                  Phone:
                      sessionData?.OwnerPhoneNumber ||
                      sessionData?.Phone ||
                      sessionData?.phone ||
                      sessionData?.companyPhone,
                  cinNumber: sessionData?.cinNumber,
                  Website: sessionData?.Website || sessionData?.website,
                  country: sessionData?.country,
                  state: sessionData?.state,
                  CityDistrict: sessionData?.CityDistrict || sessionData?.cityDistrict,
                  ReferralCode: sessionData?.ReferralCode,
                  OwnerPhoneNumber: sessionData?.OwnerPhoneNumber,
                  OwnerCountryCode: sessionData?.OwnerCountryCode,
                  CompCountryCode: sessionData?.CompCountryCode,
                  PostalCode: sessionData?.PostalCode || sessionData?.postalCode,
              }
            : reduxProfile
            ? {
                  ...initialFormData,
                  FirstName: reduxProfile.firstName,
                  LastName: reduxProfile.lastName,
                  Email: reduxProfile.email,
                  ReferralCode: referralValue,
              }
            : initialFormData;
    }, [sessionData, reduxProfile]);

    const sessionFormDataStep3 = useMemo(
        () =>
            sessionDataStep3
                ? {
                      ...sessionData,
                      // CompanyAddress: sessionDataStep3?.CompanyAddress,
                      // Address2: sessionDataStep3?.Address2,
                      country: sessionDataStep3?.country,
                      state: sessionDataStep3?.state,
                      CityDistrict: sessionDataStep3?.CityDistrict,
                      PostalCode: sessionDataStep3?.PostalCode,
                      IsCompanyTermsChecked: sessionDataStep3?.IsCompanyTermsChecked,
                      recaptcha: sessionDataStep3?.recaptcha,
                      // ReferralCode: sessionDataStep3?.ReferralCode,
                  }
                : initialFormData,
        [sessionDataStep3, sessionData]
    );

    const initialErrors = useInitialErrors(
        sessionFormData,
        getValidationSchema(isStateValueAvailable, isCityValueAvailable, captchaEnabled)
    );

    const submitForm = useCallback(
        async (values: ManagerFormsType) => {
            // const addressData = values.CompanyAddress + '%' + values.Address2;

            const requestData: ManagerRequestType = {
                ...values,
                Phone: values.Phone,
                CityDistrict: values.CityDistrict === '' ? null : values.CityDistrict,
                state: values.state === '' ? null : values.state,
                CompanyAddress: '',
                OwnerPhoneNumber: values.OwnerPhoneNumber,
                GstNumber: values.GstNumber === '' ? null : values.GstNumber,
                AuthType: passString ? SocialEnum.CustomAuth : reduxProfile?.authType!,
                Token: reduxProfile?.token! || '',
                Password: (passString && passString) || '',
                MiddleName: '',
                isAffiliate: disableReferral || false,
                companyStatus: sessionData?.CompanyStatus || CompanyStatus.Joined,
                companyType: sessionData?.CompanyType || CompanyType.Default,
                companyId: sessionData?.Id || '',
            };

            try {
                const data = await authService.managerSignUp(requestData);
                if (data) {
                    dispatch(deleteReduxProfile());
                    localStorageRemoveItem('Password');
                    localStorageRemoveItem('referral');
                    localStorageRemoveItem('Email');
                    // sessionStorageRemoveItem('user-basic-details');
                    setHasUnsavedChanges(false);
                    secureLocalStorage.removeItem(USER_DATA_CACHE_KEY);
                    sessionStorageClearAll();
                    if (seoSearchPresent?.url) {
                        if (seoSearchPresent?.isCandidate) {
                            await goToRoom(data, dispatch, navigate, {
                                isCandidate: true,
                                candidateFirstName: seoSearchPresent?.data.firstName,
                                candidateLastName: seoSearchPresent?.data.lastName,
                                hashedId: seoSearchPresent?.data?.hashId,
                            });
                        } else {
                            await goToRoom(data, dispatch, navigate, {
                                isCandidate: false,
                                companyName: seoSearchPresent?.data?.name,
                                hashedId: seoSearchPresent?.data?.hashId,
                            });
                        }
                    } else {
                        await goToRoom(data, dispatch, navigate);
                    }
                }
            } catch (err) {}
        },
        [passString, reduxProfile?.authType, reduxProfile?.token, dispatch, disableReferral, sessionData]
    );

    // Join company by invitation
    useEffect(() => {
        if (!managerId || !reduxProfile) return;
        (async function () {
            try {
                await authService.managerJoinByInvitation({
                    Email: reduxProfile.email,
                    InvitationId: managerId,
                });
                const data = await authService.socialSignIn({
                    Email: reduxProfile.email,
                    AuthType: reduxProfile.authType,
                    Token: reduxProfile.token,
                    Location: registrationLocationConstants.IsSocial,
                });
                if (data) {
                    dispatch(deleteReduxProfile());
                    // sessionStorageRemoveItem('firstTimeOpen');
                    setHasUnsavedChanges(false);
                    secureLocalStorage.removeItem(USER_DATA_CACHE_KEY);
                    sessionStorageClearAll();
                    await goToRoom(data, dispatch, navigate);
                }
            } catch (err) {}
        })();
    }, [managerId, dispatch, reduxProfile]);

    const onCountrySelectionChange = (countryId: string) => {
        setSelectedCountryId(countryId);
    };

    const displayCountryPolicyDocument = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        if (selectedCountryId) setOpenLegalDocModal(true);
        else setOpenLegalDocModal(false);
    };

    // Function to store form values in local storage
    const storeFormValuesInLocalStorage = (values: any) => {
        setHasUnsavedChanges(true);
        let previousValues = secureLocalStorage.getItem(USER_DATA_CACHE_KEY) || {};
        secureLocalStorage.setItem(USER_DATA_CACHE_KEY, {
            ...(previousValues as any),
            ...values,
        });

        // localStorageSetItem("sample-test", JSON.stringify(secureLocalStorage.getItem(USER_DATA_CACHE_KEY)));
    };

    useEffect(() => {
        if (referralValue) {
            setDisableReferral(true);
        }

        if (!consentWorkFlowSettings?.settings) dispatch(getConsentWorkflowSettings());

        const data2 = sessionStorageGetItem('managerStep3', false)!;

        delete data2?.recaptcha;
        setSessionDataStep3(data2);
        setSelectedCountryId(data2?.country);

        const CustomAuthPass = localStorageGetItem('Password')!;

        if (CustomAuthPass) {
            setPassString(CustomAuthPass);
        }
    }, []);

    return (
        <>
            <UnsavedChanges
                customEmail={customEmail}
                hasUnsavedChanges={hasUnsavedChanges}
                reduxProfile={reduxProfile}
                sessionData={sessionData}
                setIso2CountryString={setIso2CountryString}
                setSessionData={setSessionData}
                setUseCacheData={setUseCacheData}
            />
            <Formik
                innerRef={ref}
                onSubmit={submitForm}
                enableReinitialize
                initialValues={sessionFormData as any}
                validationSchema={getValidationSchema(isStateValueAvailable, isCityValueAvailable, captchaEnabled)}
                initialErrors={initialErrors}
            >
                {(formikProps: FormikProps<ManagerFormsType>) => {
                    const { handleSubmit, values, isSubmitting, setFieldTouched, setFieldValue } = formikProps;
                    const siteKey = getSiteKey();

                    return (
                        <form onSubmit={handleSubmit}>
                            <div className="fs-16 fw-600 mt-3 mb-3 lt-text-secondary">
                                Give us basic details about your organization
                            </div>
                            {selectedAuthType !== 'customAuth' && (
                                <div className="card lt-shadow-sm mb-4">
                                    <div className="card-body">
                                        <div className="container-fluid">
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <div className="fs-18 fw-600 mb-3">User Details</div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-4">
                                                        <label className="fs-14 fw-400 mb-2" htmlFor="fname">
                                                            First Name <span className="lt-text-error">*</span>
                                                        </label>
                                                        <Field name="FirstName">
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);
                                                                return (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            value={field.value}
                                                                            onChange={(ev) => {
                                                                                const regex = /^[a-zA-Z0-9 ]*$/;

                                                                                if (
                                                                                    regex.test(ev.target.value.trim())
                                                                                ) {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                    storeFormValuesInLocalStorage({
                                                                                        ...values,
                                                                                        [field.name]: ev.target.value,
                                                                                    });
                                                                                }
                                                                            }}
                                                                            className={`form-control ${
                                                                                error ? 'is-invalid' : ''
                                                                            }`}
                                                                            placeholder="Enter Your First Name"
                                                                            maxLength={100}
                                                                            id="fname"
                                                                        />
                                                                        {typeof error === 'string' && (
                                                                            <small className="text-danger fs-12">
                                                                                {error}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-4">
                                                        <label className="fs-14 fw-400 mb-2" htmlFor="mname">
                                                            Middle Name
                                                        </label>
                                                        <Field name="MiddleName">
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);
                                                                return (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            value={field.value}
                                                                            onChange={(ev) => {
                                                                                const regex = /^[a-zA-Z0-9 ]*$/;

                                                                                if (regex.test(ev.target.value)) {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                    storeFormValuesInLocalStorage({
                                                                                        ...values,
                                                                                        [field.name]: ev.target.value,
                                                                                    });
                                                                                }
                                                                            }}
                                                                            className={`form-control ${
                                                                                error ? 'is-invalid' : ''
                                                                            }`}
                                                                            placeholder="Enter Your Middle Name"
                                                                            maxLength={100}
                                                                            id="mname"
                                                                        />
                                                                        {typeof error === 'string' && (
                                                                            <small className="text-danger fs-12">
                                                                                {error}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="mb-4">
                                                        <label className="fs-14 fw-400 mb-2" htmlFor="lname">
                                                            Last Name <span className="lt-text-error">*</span>
                                                        </label>
                                                        <Field name="LastName">
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);
                                                                return (
                                                                    <>
                                                                        <input
                                                                            type="text"
                                                                            value={field.value}
                                                                            onChange={(ev) => {
                                                                                const regex = /^[a-zA-Z0-9 ]*$/;

                                                                                if (regex.test(ev.target.value)) {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                    storeFormValuesInLocalStorage({
                                                                                        ...values,
                                                                                        [field.name]: ev.target.value,
                                                                                    });
                                                                                }
                                                                            }}
                                                                            className={`form-control ${
                                                                                error ? 'is-invalid' : ''
                                                                            }`}
                                                                            placeholder="Enter Your Last Name"
                                                                            maxLength={100}
                                                                            id="lname"
                                                                        />
                                                                        {typeof error === 'string' && (
                                                                            <small className="text-danger fs-12">
                                                                                {error}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6">
                                                    <div className="mb-4">
                                                        <label className="fs-14 fw-400 mb-2" htmlFor="contact">
                                                            Contact Number <span className="lt-text-error">*</span>
                                                        </label>

                                                        <div className="d-flex">
                                                            <Field name="OwnerCountryCode">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);
                                                                    return (
                                                                        <>
                                                                            <div className="">
                                                                                <PhoneCodeDropdown
                                                                                    id="companyUser"
                                                                                    companyUserValue={field.value}
                                                                                    setFieldValue={setFieldValue}
                                                                                    onPhoneCodeSelectionChange={(
                                                                                        code: any
                                                                                    ) => {
                                                                                        storeFormValuesInLocalStorage({
                                                                                            ...values,
                                                                                            OwnerCountryCode: code,
                                                                                        });
                                                                                    }}
                                                                                />
                                                                            </div>
                                                                        </>
                                                                    );
                                                                }}
                                                            </Field>

                                                            <Field name="OwnerPhoneNumber">
                                                                {(fieldProps: FieldProps) => {
                                                                    const { field, form } = fieldProps;
                                                                    const error =
                                                                        getValue(form.touched, field.name) &&
                                                                        getValue(form.errors, field.name);

                                                                    if (error) {
                                                                        setUserPhoneCodeError(error.toString());
                                                                    } else {
                                                                        setUserPhoneCodeError('');
                                                                    }
                                                                    return (
                                                                        <>
                                                                            <input
                                                                                type="text"
                                                                                value={field.value}
                                                                                onChange={(ev) => {
                                                                                    const regex = /^\d+$/;

                                                                                    if (regex.test(ev.target.value)) {
                                                                                        setFieldTouched(field.name);
                                                                                        setFieldValue(
                                                                                            field.name,
                                                                                            ev.target.value
                                                                                        );
                                                                                        storeFormValuesInLocalStorage({
                                                                                            ...values,
                                                                                            OwnerPhoneNumber:
                                                                                                ev.target.value,
                                                                                        });
                                                                                    } else if (ev.target.value === '') {
                                                                                        setFieldValue(field.name, '');
                                                                                    }
                                                                                }}
                                                                                className={`form-control form-phone-border ${
                                                                                    error
                                                                                        ? 'phone-input is-invalid'
                                                                                        : ''
                                                                                }`}
                                                                                placeholder="Enter Your Contact Number"
                                                                                maxLength={10}
                                                                                id="contact"
                                                                            />
                                                                        </>
                                                                    );
                                                                }}
                                                            </Field>
                                                        </div>
                                                        <small className="text-danger fs-12">
                                                            {userPhoneCodeError}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="card lt-shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="container-fluid">
                                        <div className="row mb-4">
                                            <div className="col-md-12">
                                                <div className="fs-18 fw-600 mb-3">Company Details</div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2" htmlFor="compName">
                                                        Company Name <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="CompanyName">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        maxLength={350}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                [field.name]: ev.target.value,
                                                                            });
                                                                        }}
                                                                        className={`form-control ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Company Name"
                                                                        id="compName"
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2" htmlFor="compWeb">
                                                        Company Website <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="Website">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                [field.name]: ev.target.value,
                                                                            });
                                                                        }}
                                                                        className={`form-control  ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Company Website"
                                                                        maxLength={80}
                                                                        id="compWeb"
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-0">
                                                    <label className="fs-14 fw-400 mb-2" htmlFor="compEmail">
                                                        Company Email <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="CompanyEmail">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                [field.name]: ev.target.value,
                                                                            });
                                                                        }}
                                                                        className={`form-control ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Company Email"
                                                                        maxLength={320}
                                                                        id="compEmail"
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                    <div className="text-danger mb-2" />
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-0">
                                                    <label className="fs-14 fw-400 mb-2" htmlFor="compContact">
                                                        Company Contact Number <span className="lt-text-error">*</span>
                                                    </label>
                                                    <div className="d-flex">
                                                        <Field name="CompCountryCode">
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);
                                                                return (
                                                                    <>
                                                                        <div className="">
                                                                            <PhoneCodeDropdown
                                                                                id="company"
                                                                                companyValue={field.value}
                                                                                setFieldValue={setFieldValue}
                                                                                onPhoneCodeSelectionChange={(
                                                                                    code: any
                                                                                ) => {
                                                                                    storeFormValuesInLocalStorage({
                                                                                        ...values,
                                                                                        CompCountryCode: code,
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>

                                                        <Field name="Phone">
                                                            {(fieldProps: FieldProps) => {
                                                                const { field, form } = fieldProps;
                                                                const error =
                                                                    getValue(form.touched, field.name) &&
                                                                    getValue(form.errors, field.name);

                                                                if (error) {
                                                                    setComPhoneCodeError(error.toString());
                                                                } else {
                                                                    setComPhoneCodeError('');
                                                                }
                                                                return (
                                                                    <>
                                                                        <input
                                                                            type="input"
                                                                            value={field.value}
                                                                            onChange={(ev) => {
                                                                                const regex = /^\d+$/;

                                                                                if (regex.test(ev.target.value)) {
                                                                                    setFieldTouched(field.name);
                                                                                    setFieldValue(
                                                                                        field.name,
                                                                                        ev.target.value
                                                                                    );
                                                                                    storeFormValuesInLocalStorage({
                                                                                        ...values,
                                                                                        Phone: ev.target.value,
                                                                                    });
                                                                                } else if (ev.target.value === '') {
                                                                                    setFieldValue(field.name, '');
                                                                                }
                                                                            }}
                                                                            className={`form-control form-phone-border ${
                                                                                error ? 'phone-input is-invalid' : ''
                                                                            }`}
                                                                            placeholder="Enter Contact Number"
                                                                            maxLength={10}
                                                                            id="compContact"
                                                                        />
                                                                        {/* <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small> */}
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>
                                                    </div>
                                                    <small className="text-danger fs-12">{comPhoneCodeError}</small>
                                                </div>
                                            </div>
                                            {/* <div className="col-md-12">
                                                <div>
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Company LinkedIn Page (Optional){' '}
                                                    </label>
                                                    <Field name="LinkedINUrl">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);

                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                        }}
                                                                        className={`form-control ${error ? 'is-invalid' : ''
                                                                            }`}
                                                                        placeholder="Enter Your Company LinkedIn Page URL"
                                                                        maxLength={50}
                                                                    />
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Company Location */}
                            <div className="card lt-shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="container-fluid">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="fs-18 fw-600 mb-3">Company Location</div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Country <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="country">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);

                                                            return (
                                                                <>
                                                                    <CountryDropdown
                                                                        id="country"
                                                                        setFieldValue={setFieldValue}
                                                                        countryFieldValue={field.value}
                                                                        setStateDisabled={setStateDisabled}
                                                                        setCityDisabled={setCityDisabled}
                                                                        setIso2CountryString={setIso2CountryString}
                                                                        onCountrySelectionChange={(country: any) => {
                                                                            onCountrySelectionChange(
                                                                                country.id.toString()
                                                                            );
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                country: country.id.toString(),
                                                                                CountryName: country.name,
                                                                            });
                                                                        }}
                                                                        error={field.value === '' ? error : ''}
                                                                    />

                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {' '}
                                                                            {field.value === '' ? error : ''}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        State <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="state">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <StateDropdown
                                                                        id="state"
                                                                        setFieldValue={setFieldValue}
                                                                        stateFieldValue={field.value}
                                                                        setIsStateValueAvailable={
                                                                            setIsStateValueAvailable
                                                                        }
                                                                        setIsCityValueAvailable={
                                                                            setIsCityValueAvailable
                                                                        }
                                                                        setStateDisabled={setStateDisabled}
                                                                        stateDisabled={stateDisabled}
                                                                        setCityDisabled={setCityDisabled}
                                                                        Iso2CountryString={iso2CountryString}
                                                                        setIso2StateString={setIso2StateString}
                                                                        error={
                                                                            isStateValueAvailable && field.value === ''
                                                                                ? error
                                                                                : ''
                                                                        }
                                                                        onStateSelectionChange={(state: any) => {
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                state: state.id.toString(),
                                                                                StateName: state.name,
                                                                            });
                                                                        }}
                                                                    />

                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {' '}
                                                                            {isStateValueAvailable && field.value === ''
                                                                                ? error
                                                                                : ''}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        City <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="CityDistrict">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <CityDropdown
                                                                        id="city"
                                                                        setFieldValue={setFieldValue}
                                                                        cityFieldValue={field.value}
                                                                        setIsCityValueAvailable={
                                                                            setIsCityValueAvailable
                                                                        }
                                                                        setCityDisabled={setCityDisabled}
                                                                        cityDisabled={cityDisabled}
                                                                        iso2CountryString={iso2CountryString}
                                                                        iso2StateString={iso2StateString}
                                                                        error={
                                                                            isCityValueAvailable && field.value === ''
                                                                                ? error
                                                                                : ''
                                                                        }
                                                                        onCitySelectionChange={(city: any) => {
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                CityDistrict: city.id.toString(),
                                                                                CityName: city.name,
                                                                            });
                                                                        }}
                                                                    />

                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {' '}
                                                                            {isCityValueAvailable && field.value === ''
                                                                                ? error
                                                                                : ''}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2" htmlFor="zip">
                                                        Postal / Zip Code <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="PostalCode">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                [field.name]: ev.target.value,
                                                                            });
                                                                        }}
                                                                        className={`form-control ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Postal / Zip Code"
                                                                        maxLength={10}
                                                                        id="zip"
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2" htmlFor="referral">
                                                        Referral Code{' '}
                                                        <i className="bi bi-info-circle lt-tooltip">
                                                            <span className="lt-tooltiptext lt-shadow-md fw-400 fs-14 bottom-tip">
                                                                A referral code is a unique identifier that grants{' '}
                                                                <br /> exclusive benefits to the person who owns the{' '}
                                                                <br /> code when it is entered during the signup
                                                                process.
                                                            </span>
                                                        </i>
                                                    </label>
                                                    <Field name="ReferralCode">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        disabled={disableReferral}
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                            storeFormValuesInLocalStorage({
                                                                                ...values,
                                                                                [field.name]: ev.target.value,
                                                                            });
                                                                        }}
                                                                        className={`form-control ${
                                                                            error ? 'is-invalid' : ''
                                                                        }`}
                                                                        placeholder="Enter Referral Code (Optional)"
                                                                        maxLength={50}
                                                                        id="referral"
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>

                                            {/* <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Flat / Building <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="CompanyAddress">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            const forbiddenPattern = /%/;

                                                                            if (
                                                                                !forbiddenPattern.test(ev.target.value)
                                                                            ) {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ev.target.value
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`form-control ${error ? 'is-invalid' : ''
                                                                            }`}
                                                                        placeholder="Enter Flat / Building"
                                                                        maxLength={250}
                                                                    />
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Street Address <span className="lt-text-error">*</span>
                                                    </label>
                                                    <Field name="Address2">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            const forbiddenPattern = /%/;

                                                                            if (
                                                                                !forbiddenPattern.test(ev.target.value)
                                                                            ) {
                                                                                setFieldTouched(field.name);
                                                                                setFieldValue(
                                                                                    field.name,
                                                                                    ev.target.value
                                                                                );
                                                                            }
                                                                        }}
                                                                        className={`form-control ${error ? 'is-invalid' : ''
                                                                            }`}
                                                                        placeholder="Enter Street Address"
                                                                        maxLength={250}
                                                                    />
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>

                                            <div className="col-md-12">
                                                <div className="mb-4">
                                                    <label className="fs-14 fw-400 mb-2">
                                                        Referral Code{' '}
                                                        <i className="bi bi-info-circle lt-tooltip">
                                                            <span className="lt-tooltiptext lt-shadow-md fw-400 fs-14 bottom-tip">
                                                                A referral code is a unique identifier that grants <br />{' '}
                                                                exclusive benefits to the person who owns the <br /> code
                                                                when it is entered during the signup process.
                                                            </span>
                                                        </i>
                                                    </label>
                                                    <Field name="ReferralCode">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        value={field.value}
                                                                        onChange={(ev) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, ev.target.value);
                                                                        }}
                                                                        className={`form-control ${error ? 'is-invalid' : ''
                                                                            }`}
                                                                        placeholder="Enter Referral Code (Optional)"
                                                                        maxLength={50}
                                                                    />
                                                                    <small className="text-danger fs-12">{error}</small>
                                                                </>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div> */}
                                            <div className="col-md-12 mb-2">
                                                <div className="form-check my-2 custom-h-40">
                                                    <Field
                                                        disabled={
                                                            isConsentFeatureEnabled(consentWorkFlowSettings) &&
                                                            !values.country
                                                        }
                                                        className="form-check-input cursor-pointer"
                                                        name="IsCompanyTermsChecked"
                                                        type="checkbox"
                                                        id="check"
                                                        checked={values.IsCompanyTermsChecked}
                                                    ></Field>

                                                    {isConsentFeatureEnabled(consentWorkFlowSettings) ? (
                                                        <label
                                                            className="form-check-label fs-13 mt-1 cursor-pointer lt-text-secondary"
                                                            htmlFor="check"
                                                        >
                                                            I have read and agree to the{' '}
                                                            <a href="" onClick={displayCountryPolicyDocument}>
                                                                Privacy Policies,
                                                                {''}
                                                                Terms & Conditions
                                                            </a>
                                                            .
                                                        </label>
                                                    ) : (
                                                        <label
                                                            className="form-check-label fs-13 mt-1 cursor-pointer lt-text-secondary"
                                                            htmlFor="check"
                                                        >
                                                            I have read and agree to the{' '}
                                                            <Link to={routes.privacy} target="_blank">
                                                                Privacy Policies
                                                            </Link>{' '}
                                                            and{' '}
                                                            <Link to={routes.terms} target="_blank">
                                                                Terms & Conditions
                                                            </Link>
                                                            .
                                                        </label>
                                                    )}

                                                    <Field name="IsCompanyTermsChecked">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <div>
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            </div>
                                            {captchaEnabled && (
                                                <div className="col-md-12">
                                                    <Field name="recaptcha">
                                                        {(fieldProps: FieldProps) => {
                                                            const { field, form } = fieldProps;
                                                            const error =
                                                                getValue(form.touched, field.name) &&
                                                                getValue(form.errors, field.name);
                                                            return (
                                                                <div>
                                                                    <ReCAPTCHA
                                                                        sitekey={siteKey}
                                                                        onChange={(value) => {
                                                                            setFieldTouched(field.name);
                                                                            setFieldValue(field.name, value);
                                                                        }}
                                                                    />
                                                                    {typeof error === 'string' && (
                                                                        <small className="text-danger fs-12">
                                                                            {error}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            );
                                                        }}
                                                    </Field>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="my-3 d-flex flex-end flex-row-reverse">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="btn btn-primary social-next-button"
                                >
                                    {isSubmitting && <span className="spinner-border spinner-border-sm" />}Submit
                                </button>
                            </div>
                        </form>
                    );
                }}
            </Formik>
            {openLegalDocModal ? (
                <LegalDocumentModal
                    openLegalDocModal={openLegalDocModal}
                    setOpenLegalDocModal={setOpenLegalDocModal}
                    documentType="DEFAULT-PRIVATE"
                    countryId={selectedCountryId}
                />
            ) : (
                ''
            )}
        </>
    );
};

export default ManagerSingleRegistrationPage;
